<template>
  <div class="filter-price-content">
    <FilterPriceContentShimmer
      v-if="!isModal"
      :is-loading="isLoading"
    />
    <div class="filter-price-content__body">
      <div class="filter-price-content__input-group">
        <UiInput
          :value="formattedPrices[0]"
          :placeholder="fromPricePlaceholder"
          @inputValue="handleFromInputValue"
          @focusout="handleFromFocusOut"
        />
        <UiInput
          :value="formattedPrices[1]"
          :placeholder="toPricePlaceholder"
          @inputValue="handleToInputValue"
          @focusout="handleToFocusOut"
        />
      </div>
      <RangeInput
        v-if="isPriceRangeReady"
        :histogram="histogram!"
        :min="minPrice!"
        :max="maxPrice!"
        :value="rawValue"
        :is-modal="isModal"
        @input="updateRangeInput"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { UiInput } from '@experience/core-input-ui';
  import RangeInput from '../RangeInput.vue';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import { useFilterPriceContent } from '@/features/filter/price/composables/useFilterPriceContent';
  import { locales } from '../../i18n';
  import { useComponentI18n } from '@/shared/composables/useComponentI18n';
  import FilterPriceContentShimmer from '../FilterPriceContentShimmer.vue';

  interface Props {
    histogram?: Record<string, number>;
    minPrice?: number;
    maxPrice?: number;
    isModal?: boolean;
    isLoading?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    isModal: false,
    isLoading: false,
  });

  const { t } = useComponentI18n(locales);
  const filtersStore = useTourFiltersStore();

  const {
    isPriceRangeReady,
    rawValue,
    formattedPrices,
    fromPricePlaceholder,
    toPricePlaceholder,
    updateRangeInput,
    handleFromInputValue,
    handleToInputValue,
    handleFromFocusOut,
    handleToFocusOut,
  } = useFilterPriceContent({
    priceState: filtersStore.state,
    minPrice: () => props.minPrice,
    maxPrice: () => props.maxPrice,
    histogram: () => props.histogram,
    t,
  });
</script>

<style scoped lang="scss">
  @use '@/shared/styles/components/input' as input;

  .filter-price-content {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .filter-price-content__body {
    display: flex;
    flex-direction: column;
  }

  .filter-price-content__input-group {
    display: flex;

    @include input.joined-inputs;
  }
</style>
